// This file contains the Figure class. This is the internal data structure that
// We use in the figure editor.

import Connection from './Link';
import Block from './Block';
import { SIDE_PADDING, TOP_PADDING } from '@/data/constants/constantsDiagram';

/**
 * {@link DiagramObject} data formatted top be consumed by diagram editor
 * @type {DiagramObject}
 */
export default class Figure {
  /**
   * Creates a new Figure which is formatted to be consumed by the
   * diagram editor
   * @param {string} number - diagram figure number
   * @param {string} id - UUID string of figure
   * @param {boolean} overflow - Whether or not a figure is part of an overflow group
   */
  constructor(number, id, illustration = null, overflow = /[a-z]/gi.test(number)) {
    this.id = id;
    this.number = number;
    this.overflow = overflow; // Denotes if this is an overflow figure or not.
    this.editor = null;
    this.blocks = [];
    this.links = [];
    this.illustration = illustration;
  }

  /**
   * Creates new list of Blocks
   * @param {Object[]} blocks
   * @param {String} blocks[].id - A valid UUID
   * @param {String} blocks[].text - A string of text
   * @param {String} blocks[].label - A string of text
   * @param {String} blocks[].type - Must be one of CONTEXT, CONCEPT, or null
   * @param {Number} blocks[].x - Must be a number greater than -1 and less than 51
   * @param {Number} blocks[].y - Must be a number greater than -1 and less than 71
   * @param {Number} blocks[].width - Must be a number greater than -1 and less than 51
   * @param {Number} blocks[].height - Must be a number greater than -1 and less than 71
   * @param {String} blocks[].parent - Must be a valid UUID
   * @param {Object[]} blocks[].children
   * @param {Object[]} blocks[].links
   * @param {Object[]} blocks[].concepts
   */
  createBlocks(blocks) {
    this.blocks = blocks.map((b) => {
      b.x += SIDE_PADDING;
      b.y += TOP_PADDING;
      return new Block(b);
    });
  }

  /**
   * Creates a new list of Connections
   * @param {object[]} links list of links from server
   * @param {String} links[].id - UUID string
   * @param {String} links[].label - label text of string - *not currently supported*
   * @param {String} links[].head - UUID of head block
   * @param {String} links[].tail - UUID of tail block
   * @param {String} links[].type - Link type
   */
  createLinks(links) {
    this.links = links.map((l) => new Connection(l));
  }

  /**
   * Creates a new link and adds to to JSON
   * @param {Object} link mxCell edge representing a connection between 2 blocks
   */
  createNewLink(link) {
    const newLink = new Connection({
      id: link.id,
      label: link.value,
      head: link.source.id,
      tail: link.target.id,
      type: 'UNDIRECTED',
    });
    this.links.push(newLink);
    const head = this.blocks.find((b) => b.id === newLink.head);
    const tail = this.blocks.find((b) => b.id === newLink.tail);
    head.addLinkId(newLink.id);
    tail.addLinkId(newLink.id);
  }

  createNewBlock(block) {
    const { text, label } = this.extractTextAndLabels(block.value);
    const geo = block.geometry;
    const newBlock = new Block({
      id: block.id,
      x: geo.x / 10,
      y: geo.y / 10,
      width: geo.width / 10,
      height: geo.height / 10,
      text,
      label,
      parent: null,
      children: [],
      type: null,
      links: [],
      concepts: [],
    });
    this.blocks.push(newBlock);
  }

  extractTextAndLabels(value) {
    if (value.includes('\n')) {
      const split = value.split('\n');
      const text = split[0];
      const label = split[1].replace(/<u>/gi, '').replace(/<\/u>/gi, '');

      return { text, label };
    }
    const split = value.split(' ');
    const text = split[0];
    const label = split[1].replace(/<u>/gi, '').replace(/<\/u>/gi, '');

    return { text, label };
  }

  removeLink(id) {
    const index = this.links.findIndex((l) => l.id === id);
    const link = this.links[index];
    const head = this.blocks.find((b) => b.id === link.head);
    const tail = this.blocks.find((b) => b.id === link.tail);
    head.removeLink(id);
    tail.removeLink(id);
    this.links.splice(index, 1);
  }

  removeBlock(id) {
    const index = this.blocks.findIndex((b) => b.id === id);
    const deletedLinks = this.deleteLinks(this.blocks[index].links);

    this.removeDeletedLinksFromBlocks(deletedLinks);
    this.blocks.splice(index, 1);
  }

  /**
   *
   * @param {Connection[]} blockLinks
   * @returns {Object[]}
   */
  deleteLinks(blockLinks) {
    const deletedLinks = [];
    blockLinks.forEach((link) => {
      const linkIndex = this.links.findIndex((l) => l.id === link);
      const linkObject = this.links[linkIndex];
      const head = this.blocks.find((b) => b.id === linkObject.head);
      const tail = this.blocks.find((b) => b.id === linkObject.tail);
      const deletedLink = {
        id: link,
        head,
        tail,
      };
      deletedLinks.push(deletedLink);
      this.links.splice(linkIndex, 1);
    });
    return deletedLinks;
  }

  removeDeletedLinksFromBlocks(deletedLinks) {
    if (deletedLinks) {
      deletedLinks.forEach((link) => {
        link.head.removeLink(link.id);
        link.tail.removeLink(link.id);
      });
    }
  }
}
